import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DeleteAccountRequest, User } from '@mockd/shared';
import { ApiError } from '../../utils/apiClient';
import { ROUTES } from '../../routes';
import { Modal } from '../common/Modal';
import { Alert } from '../common/Alert';

interface DeleteAccountModalProps {
  isOpen: boolean;
  user: Pick<User, 'email' | 'hasPassword'>;
  onDelete: (data: DeleteAccountRequest) => Promise<void>;
  onDeleted: () => void;
  /** Passwordless accounts confirm by signing in again. */
  onReauthenticate: () => void;
  onClose: () => void;
}

type Blocker =
  | { kind: 'reauth' }
  | { kind: 'teams'; teams: { id: string; name: string }[] };

export function DeleteAccountModal({
  isOpen,
  user,
  onDelete,
  onDeleted,
  onReauthenticate,
  onClose,
}: DeleteAccountModalProps) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [blocker, setBlocker] = useState<Blocker | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setConfirmEmail('');
      setPassword('');
      setError(null);
      setBlocker(null);
    }
  }, [isOpen]);

  const confirmed = confirmEmail.trim().toLowerCase() === user.email.toLowerCase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmed) return;
    setError(null);
    setBlocker(null);

    setIsDeleting(true);
    try {
      await onDelete({ confirmEmail, ...(user.hasPassword && { password }) });
      onDeleted();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'REAUTH_REQUIRED') {
        setBlocker({ kind: 'reauth' });
      } else if (err instanceof ApiError && err.code === 'OWNS_SHARED_TEAMS') {
        const teams = (err.details as { teams?: { id: string; name: string }[] } | undefined)?.teams ?? [];
        setBlocker({ kind: 'teams', teams });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to delete your account');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete account"
      titleClassName="font-bold text-lg text-error"
      showCloseButton
    >
      <div className="text-sm space-y-2 mt-4">
        <p>This permanently deletes:</p>
        <ul className="list-disc pl-5 space-y-1 text-base-content/80">
          <li>your account and every sign-in method on it</li>
          <li>your personal projects, with their endpoints, rules, and request logs</li>
          <li>your global variables and notifications</li>
        </ul>
        <p className="text-base-content/80">
          Team projects you created stay with their team and are handed to the team's owner.
        </p>
        <p className="font-medium">This can't be undone.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        {error && <Alert variant="error">{error}</Alert>}

        {blocker?.kind === 'reauth' && (
          <Alert variant="warning" role="alert">
            <div className="flex flex-col items-start gap-2">
              <span>For your security, sign in again, then come back here to delete your account.</span>
              <button type="button" className="btn btn-sm" onClick={onReauthenticate}>
                Sign in again
              </button>
            </div>
          </Alert>
        )}

        {blocker?.kind === 'teams' && (
          <Alert variant="warning" role="alert">
            <div className="flex flex-col items-start gap-2">
              <span>
                You own a team that has other members. Remove its members or delete the team first:
              </span>
              <ul className="list-disc pl-5">
                {blocker.teams.map((team) => (
                  <li key={team.id}>
                    <Link to={ROUTES.teams.settings(team.id)} className="link">{team.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </Alert>
        )}

        <div className="fieldset">
          <label htmlFor="delete-confirm-email" className="fieldset-legend">
            <span>
              Type <span className="font-mono font-medium">{user.email}</span> to confirm
            </span>
          </label>
          <input
            id="delete-confirm-email"
            type="email"
            autoComplete="off"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            className="input w-full"
            disabled={isDeleting}
          />
        </div>

        {user.hasPassword && (
          <div className="fieldset">
            <label htmlFor="delete-password" className="fieldset-legend">
              Password
            </label>
            <input
              id="delete-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input w-full"
              disabled={isDeleting}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost" disabled={isDeleting}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-error"
            disabled={isDeleting || !confirmed || (user.hasPassword && !password)}
          >
            {isDeleting ? 'Deleting…' : 'Delete my account'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
